// This import has been flagged as unallowed for this class. It may be involved in a circular dependency loop.
// eslint-disable-next-line no-restricted-imports
import { PasswordInputResult } from "@bitwarden/auth/angular";
import { Account } from "@bitwarden/common/auth/abstractions/account.service";
import { UserId } from "@bitwarden/common/types/guid";

export class InvalidCurrentPasswordError extends Error {
  constructor() {
    super("The current password is invalid.");
  }
}

export abstract class ChangePasswordService {
  /**
   * Verifies that the current password is correct via `proofOfDecryption` before
   * calling change password & user key rotation logic.
   *
   * @param passwordInputResult credentials object received from the `InputPasswordComponent`
   * @param user the user account
   * @throws if called from a non-Web client
   * @throws if required values are not found on the `PasswordInputResult`
   * @throws `InvalidCurrentPasswordError` if `proofOfDecryption` fails (i.e. the current
   *          password is incorrect)
   */
  abstract changePasswordAndRotateUserKey(
    passwordInputResult: PasswordInputResult,
    user: Account,
  ): Promise<void>;

  /**
   * @deprecated To be removed in PM-28143
   *
   * Creates a new user key and re-encrypts all required data with it.
   * - does so by calling the underlying method on the `UserKeyRotationService`
   * - implemented in Web only
   *
   * @param currentPassword the current password
   * @param newPassword the new password
   * @param user the user account
   * @param newPasswordHint the new password hint
   * @throws if called from a non-Web client
   */
  abstract rotateUserKeyMasterPasswordAndEncryptedData(
    currentPassword: string,
    newPassword: string,
    user: Account,
    newPasswordHint: string,
  ): Promise<void>;

  /**
   * Changes the user's password by building a `PasswordRequest` object that gets POSTed to the server.
   *
   * @param passwordInputResult credentials object received from the `InputPasswordComponent`
   * @param userId the active user's `userId`
   * @throws if required values are not found on the `PasswordInputResult`
   * @throws an `InvalidCurrentPasswordError` if `proofOfDecryption` fails (i.e. if the current password is incorrect)
   * @throws if there is an error during the API call
   *
   * OLD DESCRIPTION FOR UNFLAGGED LOGIC: (the rest of this JSDoc below can be removed in PM-28143)
   *
   * Changes the user's password and re-encrypts the user key with the `newMasterKey`.
   * - Specifically, this method uses credentials from the `passwordInputResult` to:
   *   1. Decrypt the user key with the `currentMasterKey`
   *   2. Re-encrypt that user key with the `newMasterKey`, resulting in a `newMasterKeyEncryptedUserKey`
   *   3. Build a `PasswordRequest` object that gets POSTed to `"/accounts/password"`
   *
   * @param passwordInputResult credentials object received from the `InputPasswordComponent`
   * @param userId the `userId`
   * @throws if the `userId`, `currentMasterKey`, or `currentServerMasterKeyHash` is not found
   */
  abstract changePassword(passwordInputResult: PasswordInputResult, userId: UserId): Promise<void>;

  /**
   * Changes the user's password during Account Recovery by building an `UpdateTempPasswordRequest`
   * object that gets PUT to the server.
   *
   * Note that this method pertains to the "follow-up" stage of account recovery. That is, this user
   * is now changing their own password AFTER it was recently set/changed for them by another org member
   * who has the "Manage Account Recovery" permission.
   *
   * @param passwordInputResult credentials object received from the `InputPasswordComponent`
   * @param userId the active user's `userId`
   * @throws if required values are not found on the `PasswordInputResult`
   * @throws an `InvalidCurrentPasswordError` if `proofOfDecryption` fails (i.e. if the current password is incorrect)
   * @throws if there is an error during the API call
   *
   * OLD DESCRIPTION FOR UNFLAGGED LOGIC: (the rest of this JSDoc below can be removed in PM-28143)
   *
   * Changes the user's password and re-encrypts the user key with the `newMasterKey`.
   * - Specifically, this method uses credentials from the `passwordInputResult` to:
   *   1. Decrypt the user key with the `currentMasterKey`
   *   2. Re-encrypt that user key with the `newMasterKey`, resulting in a `newMasterKeyEncryptedUserKey`
   *   3. Build a `PasswordRequest` object that gets PUTed to `"/accounts/update-temp-password"` so that the
   *        ForcePasswordReset gets set to false.
   * @param passwordInputResult
   * @param userId
   */
  abstract changePasswordForAccountRecovery(
    passwordInputResult: PasswordInputResult,
    userId: UserId,
  ): Promise<void>;

  /**
   * Optional method that will clear up any deep link state.
   * - Currently only used on the web change password service.
   */
  clearDeeplinkState?: () => Promise<void>;

  /**
   * Optional method that closes the browser extension popout if in a popout
   * If not in a popout, does nothing.
   */
  abstract closeBrowserExtensionPopout?(): void;

  /**
   * Optional method that indicates if we should navigate to the root page of the app after a password change.
   */
  abstract shouldNavigateToRoot(): boolean;
}
